import {
  ActionIcon,
  Anchor,
  Avatar,
  CloseButton,
  CopyButton,
  Flex,
  Group,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import MainTable from "../../../../../layouts/Table/MainTable";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import { useLanguage } from "../../../../../stores/LanguageStore";
import { useCallback, useMemo, useState } from "react";
import SelectionDeleter from "../../../../../components/TableComponents/SelectionDeleter";
import {
  deleteApiKey,
  editApiKey,
  getApiKeys,
} from "../../../../../api/ConfigsApi";
import { useListingLoader } from "../../../../../components/hooks/ListingHooks";
import dateColumn from "../../../../../layouts/Table/columns/dateColumn";
import numberColumn from "../../../../../layouts/Table/columns/numberColumn";
import { nameColumn } from "../../../../../layouts/Table/columns/nameColumn";
import { IconCheck, IconChevronDown, IconCopy } from "@tabler/icons-react";
import ApiKeyRegenerate from "../../../api/ApiKeyRegenerate";
import NoItemsModal from "../../../../../components/TableComponents/NoItemsModal";
import AddApiKey from "./AddApiKey";
import { useNavigate, useParams } from "react-router-dom";
import { useListStore } from "../../../../../stores/ListStore";
import {
  getRowClasses,
  minimizedSize,
} from "../../../lists/ListingWithContent/listing";
import ApiEdit from "./ApiEdit";

const APIKeyListing = ({ loading, setLoading }) => {
  const apiRef = useGridApiRef();
  const { apiId } = useParams();
  const api_id = useMemo(() => parseInt(apiId) || null, [apiId]);
  const lang = useLanguage((s) => s.language);

  const [rows, setRows] = useState([]);

  const columns = useMemo(() => {
    if (api_id) {
      return [
        nameColumn({
          disableLink: true,
          width: 290,
          editFunction: () => editApiKey,
          handleClick: () => {},
          editorStyle: { right: 16 },
          apiRef,
          leftSection: function (row) {
            return (
              <Avatar
                color="initials"
                name={row.name}
                radius={"md"}
                size={36}
                variant="filled"
              />
            );
          },
        }),
        dateColumn(),
      ];
    }
    return [
      numberColumn(),
      nameColumn({
        handleClick: () => {},
        editFunction: () => editApiKey,
        disableLink: true,
        apiRef,
        width: 300,
        getDisabled: () => false,
      }),
      {
        field: "token",
        headerName: lang.api_keys.columns.key,
        width: 300,

        renderCell: ({ value }) => (
          <Group wrap="nowrap" w={"100%"}>
            <Anchor c={"black"} truncate="end">
              {value}
            </Anchor>
            <CopyButton value={value}>
              {({ copied, copy }) => (
                <Tooltip
                  label={copied ? lang.global.copied : `${lang.global.copy}`}
                  color={copied ? "green" : "black"}
                >
                  <ActionIcon
                    size={"md"}
                    variant="light"
                    color={copied ? "green" : "primary"}
                    onClick={(e) => {
                      e.stopPropagation();
                      copy();
                    }}
                  >
                    {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>
          </Group>
        ),
      },
      dateColumn(),
    ];
  }, [
    apiRef,
    api_id,
    lang.api_keys.columns.key,
    lang.global.copied,
    lang.global.copy,
  ]);

  const actionsCell = useCallback(
    ({ row }) => {
      return (
        <Group wrap="nowrap" gap={4}>
          <ApiKeyRegenerate row={row} setTableLoading={setLoading} />

          <IconChevronDown
            size={24}
            className="bump"
            opacity={api_id === row.id ? 1 : 0.4}
            style={{
              transform: api_id === row.id ? "rotate(-90deg)" : "none",
              transition: "all ease .3s",
            }}
            onClick={() => handleRowClick({ row })}
          />
        </Group>
      );
    },
    [api_id]
  );

  const toolbarActions = useCallback(
    ({ selectedIds }) => (
      <SelectionDeleter
        handleDelete={deleteApiKey}
        apiRef={apiRef}
        setTableLoading={setLoading}
        ids={selectedIds}
      />
    ),
    [apiRef]
  );

  const navigate = useNavigate();
  const handleRowClick = ({ row }) => {
    const setMinimized = useListStore.getState().setModelMinimized;

    if (api_id === row.id) {
      navigate(`/settings/integrations/api/none`);
    } else {
      navigate(`/settings/integrations/api/${row.id}`);
      setMinimized(true);
    }
  };

  const handleClose = () => {
    navigate(`/settings/integrations/api/none`);
  };

  useListingLoader({
    loading,
    setLoading,
    listGetter: getApiKeys,
    responseField: "api_keys_list",
    setRows,
  });

  return (
    <Group
      flex={1}
      wrap="nowrap"
      align="stretch"
      gap={0}
      style={{ overflowX: "hidden" }}
      pt={64}
      mt={-64}
      pos={"relative"}
    >
      <Flex flex={1} maw={api_id ? minimizedSize - 48 : null} pos={"relative"}>
        <NoItemsModal
          rows={rows}
          loading={loading}
          text={
            <Text size="xl" mb={32}>
              {lang.settings.no_api}
            </Text>
          }
          button={<AddApiKey onFinish={() => setLoading(true)} />}
        />
        <MainTable
          apiRef={apiRef}
          columns={columns}
          rows={rows}
          loading={loading}
          setLoading={setLoading}
          id={"model-table"}
          toolbarActions={api_id ? null : toolbarActions}
          actionsCell={actionsCell}
          onRowClick={handleRowClick}
          whiteButtons={!api_id}
          actionsCellWidth={75}
          disableGotoPage={api_id}
          disableVisibleColumns={api_id}
          defaultPerPage={15}
          disablePerPage={api_id}
          disableCheckboxes={!!api_id}
          getRowClassName={(params) =>
            getRowClasses({ params, mainId: apiId, integerId: api_id })
          }
          withSorter={!!api_id}
          tableSx={{
            "& .MuiDataGrid-columnHeaders": {
              border: "none !important",
              display: api_id ? "none" : "flex",
            },
            "& .MuiDataGrid-cell": {
              border: "none",
            },
            "& .MuiDataGrid-main": {
              background: "var(--mantine-color-primary-2)",
              border: "none !important",
              borderRadius: 3,
            },
            "& .table-search": {
              width: api_id ? "200px !important" : "300px !important",
            },
            "& .MuiDataGrid-footerContainer": {
              border: "none !important",
            },
            "& .table-row": {
              cursor: "pointer",
              borderRadius: 3,
              marginBottom: 1,
              background: "#fff",
              border: "1px solid var(--mantine-color-primary-1)",
            },
            "& .MuiDataGrid-pinnedColumnHeaders": {
              background: "transparent",
              borderRight: "none !important",
            },
            "& .MuiDataGrid-pinnedColumns--right": {
              background: "var(--mantine-color-primary-2) !important",
              border: "none !important",
            },
            "& .MuiDataGrid-pinnedColumns--left": {
              background: "var(--mantine-color-primary-2) !important",
              border: "none !important",
            },
            "& .MuiDataGrid-pinnedColumns--right .table-row": {
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              background: "#fff",
              border: "1px solid   var(--mantine-color-primary-1)",
              borderLeft: "none !important",
            },
            "& .MuiDataGrid-pinnedColumns--left .table-row": {
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
              background: "#fff",
              border: "1px solid   var(--mantine-color-primary-1)",
              borderRight: "none !important",
            },
            "& .MuiDataGrid-virtualScrollerRenderZone .table-row": {
              borderRadius: api_id ? 3 : 0,
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
            },
            "& .MuiDataGrid-selectedRowCount": {
              visibility: api_id ? "hidden" : "visible",
            },
            "& .MuiPagination-root": {
              left: api_id ? 0 : undefined,
              transform: api_id ? "none" : undefined,
            },
          }}
        />
      </Flex>

      <Stack
        flex={1}
        maw={api_id ? "none" : 0}
        pl={32}
        style={{ transition: "all ease .3s", overflow: "hidden" }}
        h={"auto"}
        pos={"relative"}
      >
        <CloseButton
          style={{
            position: "absolute",
            left: 48,
            top: 8,
            zIndex: 4,
          }}
          size={40}
          onClick={handleClose}
        />
        {api_id && !loading && <ApiEdit />}
      </Stack>
    </Group>
  );
};

export default APIKeyListing;
